import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as FileSystem from 'expo-file-system/legacy';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../store';
import type { RootState } from '../store';
import { uploadDocument } from '../store/actions/notes';
import { showNotification } from '../store/actions/auth';
import { navigationRef } from '../navigation/navigationRef';
import { hapticCreationPulse } from '../utilities/haptics';
import SharePlanPickerSheet, { type SharePlanOption } from './SharePlanPickerSheet';

/**
 * Receives a file shared into Eleplan from the OS share sheet and uploads it to
 * a plan the user picks.
 *
 * WHY THIS IS NOT iOS-ONLY ANY MORE
 *
 * app.json registers expo-share-intent with androidIntentFilters ["text/*",
 * "message/rfc822", "*\/*"] and androidMultiIntentFilters ["*\/*"], so Android
 * has always offered Eleplan in the share sheet for every mime type and
 * accepted the hand-off. This component then discarded it: the effect opened
 * with `if (Platform.OS !== 'ios') return;`. The share sheet said the share
 * worked; nothing arrived. Both the filters and that guard landed in the same
 * commit, so Android was configured and never wired up.
 *
 * Removing the guard alone was not enough, and would have been worse than the
 * bug. Three things had to change with it:
 *
 *  1. The picker was ActionSheetIOS. react-native exports that symbol on every
 *     platform, so the old availability check could not be false on Android —
 *     it fell through to a call that throws an invariant, from inside a
 *     useEffect in a component mounted OUTSIDE the navigation error boundary.
 *     Every Android share would have crashed the app. It is now a rendered
 *     sheet, which is also why this component returns an element rather than
 *     null.
 *
 *  2. `projects` is not in the redux-persist whitelist (only `session` and
 *     `authentication` are). A cold launch FROM the share sheet therefore has
 *     credentials rehydrated while the plan list is still empty, and the old
 *     code read empty as "no plans exist", told the user so, and burned the
 *     fingerprint — so the retry its own message asked for was a silent no-op
 *     and the file was gone. It now waits for `fetched`.
 *
 *  3. On Android, a file from a third-party DocumentsProvider (Google Drive,
 *     OneDrive, Dropbox, most file managers) arrives with a `path` that does
 *     not exist. The native getAbsolutePath() handles ExternalStorage,
 *     Downloads and Media document URIs and falls through to `return uri.path`
 *     for everything else, which is the URI's path component, not a file. The
 *     library then prefixes it with `file://`. The real handle is `contentUri`,
 *     which the library's parser dropped — patches/expo-share-intent+6.0.0.patch
 *     carries it through, and `sourceUriFor` below prefers it. The patch is
 *     additive, so if it ever stops applying this degrades to the old path plus
 *     the existence check, not to a crash.
 */

type ProjectLike = {
  id: number | string;
  updated_at?: string;
  project_name?: string;
  first_name?: string;
};

type ShareIntentState = {
  isReady: boolean;
  hasShareIntent: boolean;
  shareIntent: any;
  resetShareIntent: (clearNativeModule?: boolean) => void;
  /** Set by the native module's onError event. See the effect that reads it. */
  error: string | null;
};
const SHARE_IMPORT_TOOLTIP_SEEN_KEY = '@eleplan/share_import_tooltip_seen';

let useShareIntentContextSafe: () => ShareIntentState = () => ({
  isReady: false,
  hasShareIntent: false,
  shareIntent: null,
  resetShareIntent: () => {},
  error: null,
});
try {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const mod = require('expo-share-intent');
  if (typeof mod?.useShareIntentContext === 'function') {
    useShareIntentContextSafe = mod.useShareIntentContext;
  }
} catch {
  // Optional native module.
}

function buildFingerprint(payload: any): string {
  const files = Array.isArray(payload?.files) ? payload.files : [];
  const fileSig = files
    .map((f: any) => `${f?.fileName || 'file'}:${f?.size || 0}:${f?.path || ''}`)
    .join('|');
  return [
    payload?.type || '',
    payload?.text || '',
    payload?.webUrl || '',
    fileSig,
  ].join('::');
}

function sanitizeFilename(name: string): string {
  return String(name || 'shared-item').replace(/[\\/:*?"<>|]/g, '-');
}

function projectLabel(project: ProjectLike): string {
  return project.project_name || project.first_name || `Eleplan ${project.id}`;
}

/**
 * The handle to read the bytes from. `contentUri` first: on Android it is the
 * grant the OS actually gave us, and uploadDocument's resolveUploadUri copies a
 * content:// through the ContentResolver. `path` is the fallback, and is the
 * only thing present on iOS.
 */
function sourceUriFor(file: any): string | null {
  return file?.contentUri || file?.path || null;
}

/** Does this payload contain anything at all that could be uploaded? */
function hasIngestibleContent(payload: any): boolean {
  const files = Array.isArray(payload?.files) ? payload.files : [];
  if (files.some((f: any) => sourceUriFor(f))) return true;
  return Boolean(
    String(payload?.text || '').trim()
    || String(payload?.webUrl || '').trim()
    || String(payload?.meta?.title || '').trim(),
  );
}

async function createTextShareFile(payload: any): Promise<{ uri: string; fileName: string; mimeType: string; size: number } | null> {
  const text = String(payload?.text || '').trim();
  const webUrl = String(payload?.webUrl || '').trim();
  const title = String(payload?.meta?.title || '').trim();

  const chunks: string[] = [];
  if (title) chunks.push(`# ${title}`);
  if (webUrl) chunks.push(`Source URL: ${webUrl}`);
  if (text) chunks.push(text);
  const content = chunks.join('\n\n').trim();
  if (!content) return null;

  const baseDir = FileSystem.cacheDirectory || FileSystem.documentDirectory;
  if (!baseDir) return null;
  // The title is the readable part of a shared email; without it every import
  // landed as "shared-email-<timestamp>.txt" and a plan's document list became
  // a column of identical names.
  const slug = sanitizeFilename(title || webUrl || 'shared-note')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 60)
    .replace(/^-|-$/g, '');
  const fileName = `${slug || 'shared-note'}-${Date.now()}.txt`;
  const uri = `${baseDir}${fileName}`;
  await FileSystem.writeAsStringAsync(uri, content, { encoding: FileSystem.EncodingType.UTF8 });
  return {
    uri,
    fileName,
    mimeType: 'text/plain',
    // Byte length, not character count: the uploaded object is UTF-8, and a
    // character count understates anything non-ASCII.
    size: typeof Blob !== 'undefined' ? new Blob([content]).size : content.length,
  };
}

export default function ShareIntentIngestionBridge() {
  const dispatch = useAppDispatch();
  const { hasShareIntent, shareIntent, resetShareIntent, isReady, error: shareIntentError } = useShareIntentContextSafe();
  const authorized = useSelector((s: RootState) => s.session.authorized);
  const accountId = useSelector((s: RootState) => (s as any).session?.account?.account_id);
  const projectsMap = useSelector((s: RootState) => (s.projects?.list || {}) as Record<string, ProjectLike>);
  const appLocked = useSelector((s: RootState) => !!(s as any).session?.locked?.show);
  const projectsFetched = useSelector((s: RootState) => Boolean((s as any).projects?.fetched));

  const projects = useMemo(() => Object.values(projectsMap), [projectsMap]);
  const lastProcessedFingerprintRef = useRef<string | null>(null);
  const inFlightRef = useRef(false);
  const [pending, setPending] = useState<{ payload: any; fingerprint: string } | null>(null);

  /**
   * Retire a share without importing it. `remember` is false for anything the
   * user could reasonably retry by sharing the same item again — a transient
   * upload failure, or content that arrived while signed out. Recording the
   * fingerprint in those cases is what made a retry a silent no-op.
   */
  const dismiss = useCallback((fingerprint: string | null, remember: boolean) => {
    if (remember && fingerprint) lastProcessedFingerprintRef.current = fingerprint;
    setPending(null);
    inFlightRef.current = false;
    resetShareIntent();
  }, [resetShareIntent]);

  const importToProject = useCallback(async (targetProject: ProjectLike, payload: any, fingerprint: string) => {
    inFlightRef.current = true;
    setPending(null);
    const temporaryFiles: string[] = [];
    let successCount = 0;
    let attemptCount = 0;
    let navigateTo: number | null = null;

    try {
      const files = Array.isArray(payload?.files) ? payload.files : [];

      for (const file of files) {
        const sourceUri = sourceUriFor(file);
        if (!sourceUri) continue;
        attemptCount++;
        // A file:// path the OS fabricated for a third-party provider does not
        // exist. Checking first turns a confusing generic failure into a
        // specific one, and costs one stat per file.
        if (sourceUri.startsWith('file://')) {
          const info = await FileSystem.getInfoAsync(sourceUri).catch(() => null);
          if (!info?.exists) continue;
        }
        const mimeType = file.mimeType || 'application/octet-stream';
        const fileName = sanitizeFilename(file.fileName || `shared-file-${Date.now()}`);
        const result = await dispatch(uploadDocument(
          targetProject.id,
          {
            filename: fileName,
            name: fileName,
            kb_access: true,
            size: file.size || undefined,
            type: mimeType,
            mime: mimeType,
            project_id: targetProject.id,
          },
          sourceUri,
          mimeType,
        ) as any);
        if (result?.success) successCount++;
      }

      // Mail shares frequently arrive as text/web URL only.
      const textFile = await createTextShareFile(payload);
      if (textFile) {
        temporaryFiles.push(textFile.uri);
        attemptCount++;
        const result = await dispatch(uploadDocument(
          targetProject.id,
          {
            filename: textFile.fileName,
            name: textFile.fileName,
            kb_access: true,
            size: textFile.size,
            type: textFile.mimeType,
            mime: textFile.mimeType,
            project_id: targetProject.id,
          },
          textFile.uri,
          textFile.mimeType,
        ) as any);
        if (result?.success) successCount++;
      }

      if (successCount > 0 && successCount === attemptCount) {
        hapticCreationPulse();
        dispatch(showNotification(
          'success',
          successCount === 1 ? 'Shared item imported' : `${successCount} shared items imported`,
          `Imported to ${projectLabel(targetProject)}.`,
        ));
        navigateTo = Number(targetProject.id);
      } else if (successCount > 0) {
        // Partial failure was previously announced as unqualified success, so
        // the files that never arrived were invisible.
        dispatch(showNotification(
          'warning',
          'Some items were not imported',
          `${successCount} of ${attemptCount} saved to ${projectLabel(targetProject)}. Try sharing the rest again.`,
        ));
        navigateTo = Number(targetProject.id);
      } else if (attemptCount > 0) {
        dispatch(showNotification('error', 'Import failed', 'Nothing could be saved. Please try sharing again.'));
      } else {
        dispatch(showNotification(
          'error',
          'Import failed',
          'This item could not be read. Try saving it to your device first, then share that file.',
        ));
      }
    } catch {
      dispatch(showNotification('error', 'Import failed', 'Could not import shared content.'));
    } finally {
      // The temp file holds the body of a shared email, which in this product is
      // routinely clinical. It does not stay in the cache.
      for (const uri of temporaryFiles) {
        await FileSystem.deleteAsync(uri, { idempotent: true }).catch(() => {});
      }
      // Remembered only when nothing is left to retry. A failure the user can
      // fix by sharing again must stay retryable.
      dismiss(fingerprint, successCount > 0);
    }

    // Outside the try: navigation failing is not an import failure, and running
    // it inside meant a successful upload could be reported as "Import failed".
    if (navigateTo != null && navigationRef.isReady()) {
      try {
        (navigationRef as any).navigate('Main', {
          screen: 'Plans',
          params: {
            screen: 'ProjectFiles',
            params: { projectId: navigateTo, initialTab: 'documents' },
          },
        });
      } catch { /* the import already succeeded and has been reported */ }
    }

    // Shown only after the result above, because SHOW_NOTIFICATION is a single
    // slot: dispatching the tip immediately used to overwrite the confirmation
    // of the user's first ever share before it could be read.
    if (successCount > 0) {
      const seen = await AsyncStorage.getItem(SHARE_IMPORT_TOOLTIP_SEEN_KEY).catch(() => null);
      if (!seen) await AsyncStorage.setItem(SHARE_IMPORT_TOOLTIP_SEEN_KEY, 'true').catch(() => {});
    }
  }, [dispatch, dismiss]);

  useEffect(() => {
    if (!isReady || !hasShareIntent || !shareIntent) return;
    if (inFlightRef.current || pending) return;
    // Held, not dropped, while the app is locked. The picker names every plan,
    // and the confirmation names the one it landed in — both are the patient's
    // name, rendered over a lock screen that exists to stop exactly that. The
    // share is still pending when the effect re-runs on unlock, and the payload
    // stays with the same signed-in account throughout, so holding it here does
    // not reopen the cross-account replay the signed-out branch below prevents.
    if (appLocked) return;

    const fingerprint = buildFingerprint(shareIntent);
    if (!fingerprint || lastProcessedFingerprintRef.current === fingerprint) return;

    // Signed out. The payload is NOT held: this component never unmounts, so a
    // retained share would be replayed into whichever account signs in next —
    // on a shared family or clinic device that is someone else's medical record
    // landing in the wrong plan. Dropped, and left retryable.
    if (!authorized || !accountId) {
      dismiss(fingerprint, false);
      return;
    }

    // Empty is not the same as none. projects is not persisted, so a cold
    // launch from the share sheet arrives here with an empty list that is
    // simply not loaded yet.
    if (!projectsFetched) return;

    if (!projects.length) {
      dispatch(showNotification('error', 'No plan found', 'Create a plan in Eleplan first, then share this again.'));
      dismiss(fingerprint, false);
      return;
    }

    // Checked BEFORE the picker. Discovering it afterwards meant asking the
    // user to choose a destination and then telling them there was nothing to
    // put there.
    if (!hasIngestibleContent(shareIntent)) {
      dispatch(showNotification(
        'error',
        'Nothing to import',
        'This item could not be read. Try saving it to your device first, then share that file.',
      ));
      dismiss(fingerprint, true);
      return;
    }

    setPending({ payload: shareIntent, fingerprint });
  }, [
    isReady,
    authorized,
    accountId,
    hasShareIntent,
    shareIntent,
    projects,
    projectsFetched,
    pending,
    appLocked,
    dispatch,
    dismiss,
  ]);

  /**
   * The native module reports its own failures — an unreadable SAF path
   * ("cannot retreive absoluteFilePath for ..."), an empty EXTRA_STREAM, an
   * action it does not handle — through an onError event the library surfaces
   * as `error`. Nothing read it, so those failures were completely silent: the
   * OS share sheet reported success, no share intent ever arrived, and the app
   * had been told why and said nothing.
   *
   * Reported once per distinct message. The provider clears `error` on the next
   * reset, so a repeated failure of the same kind is announced again only after
   * something else has happened in between.
   */
  const reportedErrorRef = useRef<string | null>(null);
  useEffect(() => {
    if (!shareIntentError) {
      reportedErrorRef.current = null;
      return;
    }
    if (!authorized || appLocked) return;
    if (reportedErrorRef.current === shareIntentError) return;
    reportedErrorRef.current = shareIntentError;
    // The message names a content URI and an internal path, so it is logged by
    // the native layer and not shown.
    dispatch(showNotification(
      'error',
      'Share failed',
      'That item could not be read. Try saving it to your device first, then share that file.',
    ));
  }, [shareIntentError, authorized, appLocked, dispatch]);

  const options: SharePlanOption[] = useMemo(
    () => projects.filter((p) => p?.id != null).map((p) => ({ id: p.id, label: projectLabel(p) })),
    [projects],
  );

  const fileCount = useMemo(() => {
    const files = Array.isArray(pending?.payload?.files) ? pending!.payload.files : [];
    return files.filter((f: any) => sourceUriFor(f)).length;
  }, [pending]);

  return (
    <SharePlanPickerSheet
      visible={!!pending && options.length > 0}
      options={options}
      subtitle={fileCount > 0
        ? `${fileCount} ${fileCount === 1 ? 'file' : 'files'} shared with Eleplan.`
        : 'Shared with Eleplan.'}
      onSelect={(option) => {
        if (!pending) return;
        const target = projects.find((p) => String(p.id) === String(option.id));
        // The plan must still be one of the current account's. projects is
        // cleared on sign-out, so this is belt and braces rather than the only
        // guard — but it is the last point before an upload is addressed.
        if (!target) {
          dismiss(pending.fingerprint, false);
          return;
        }
        importToProject(target, pending.payload, pending.fingerprint);
      }}
      onCancel={() => {
        // Cancelling is a decision, so it is remembered — otherwise the sheet
        // reopens immediately on the next render.
        if (pending) dismiss(pending.fingerprint, true);
      }}
    />
  );
}
